import { zodResolver } from '@hookform/resolvers/zod';
import {
  CAMPAIGN_ERROR_MESSAGES,
  MAX_CONVERSIONS_PERIOD_HOUR,
  MAX_STO_COMPLETION_TIME,
  MIN_CONVERSIONS_PERIOD_HOUR,
  MIN_RATE_LIMIT_PER_MINUTE,
  MIN_RATE_LIMIT_PER_SECOND,
  MIN_STO_COMPLETION_TIME,
} from 'modules/campaigns/lib/constants';
import { AttributionMode, RateLimitUnit } from 'modules/domains/campaign';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useSelector } from 'react-redux';
import { selectIsJourneyGoalsFeatureFlagOn } from 'core/userContext';
import { useCampaignContext } from '../../context';
import { CampaignEditorFormState } from '../../types';
import { getCampaignEditorFormInitialValues } from '../../utils';

export type FormProviderProps = {
  children: React.ReactNode;
};

const schema = z.object({
  ignoreFrequencyCap: z.boolean(),
  useQuietHours: z.boolean(),
  customQuietHours: z
    .object({
      startTime: z.string(),
      endTime: z.string(),
      sendSkip: z.boolean(),
    })
    .nullable()
    .refine((data: any) => data?.startTime !== data?.endTime, {
      message: CAMPAIGN_ERROR_MESSAGES.sameQuietHoursStartAndEndTime,
    }),
  useSendTimeOptimization: z.boolean(),
  stoCompletionTime: z
    .string()
    .optional()
    .refine(
      (stoCompletionTime?: string) =>
        !!stoCompletionTime &&
        parseInt(stoCompletionTime, 10) >= MIN_STO_COMPLETION_TIME &&
        parseInt(stoCompletionTime, 10) <= MAX_STO_COMPLETION_TIME,
      CAMPAIGN_ERROR_MESSAGES.stoCompletionTimeRange,
    ),
  useRateLimit: z.boolean(),
  campaignAlertsEnabled: z.boolean(),
  rateLimit: z
    .object({
      rateLimitValue: z.string().optional(),
      rateLimitUnit: z.string().optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
      const convertedValue = parseInt(data?.rateLimitValue ?? '', 10);
      const validatedNumber = Number.isNaN(convertedValue) ? 0 : convertedValue;
      if (
        data?.rateLimitUnit === RateLimitUnit.Minute &&
        validatedNumber < MIN_RATE_LIMIT_PER_MINUTE
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CAMPAIGN_ERROR_MESSAGES.minimumRateLimitValuePerMinute,
        });
      }

      if (
        data?.rateLimitUnit === RateLimitUnit.Second &&
        validatedNumber < MIN_RATE_LIMIT_PER_SECOND
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CAMPAIGN_ERROR_MESSAGES.minimumRateLimitValuePerSecond,
        });
      }
    }),
  conversions: z
    .object({
      useConversions: z.boolean().optional(),
      events: z.string().array().optional(),
      mode: z.nativeEnum(AttributionMode).optional(),
      periodHours: z.string().optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
      if (data?.periodHours === '')
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CAMPAIGN_ERROR_MESSAGES.invalidAttributionPeriodRange,
        });

      // we should look at refactoring these into functions that take a failure call back so we can write tests.
      const isLastMessagedAttributionMode = data?.mode === AttributionMode.LastMessaged;
      const periodHours = parseInt(data?.periodHours ?? '', 10); // returns number or NaN

      if (
        isLastMessagedAttributionMode &&
        (periodHours < MIN_CONVERSIONS_PERIOD_HOUR || periodHours > MAX_CONVERSIONS_PERIOD_HOUR)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CAMPAIGN_ERROR_MESSAGES.invalidAttributionPeriodRange,
        });
      }
    }),
  priorityLevel: z.number().optional(),
});

type CampaignEditorFormSchema = Required<z.infer<typeof schema>>;

export const PrimaryContentProvider = ({ children }: FormProviderProps) => {
  const { campaign } = useCampaignContext();
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const initialValues = useMemo<CampaignEditorFormState>(
    () => getCampaignEditorFormInitialValues(campaign, isJourneyGoalsEnabled),
    [campaign, isJourneyGoalsEnabled],
  );

  const methods = useForm<CampaignEditorFormSchema>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const { reset } = methods;

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  return <FormProvider {...methods}>{children}</FormProvider>;
};
